import { useMemo, useState } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Chip } from '@/ui/primitives/Chip';
import { CoinGlyph } from '@/ui/primitives/CoinGlyph';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { homeRepo } from '@/data/repositories/home-repo';
import { fmt, fmtDateTime } from '@/ui/utils/format';
import type { DepositRecord, WithdrawRecord } from '@/domain/types';

type Kind = 'all' | 'deposit' | 'withdraw';
type WalletItem =
  | { kind: 'deposit'; id: string; time: number; record: DepositRecord }
  | { kind: 'withdraw'; id: string; time: number; record: WithdrawRecord };

const filters: Array<{ k: Kind; l: string }> = [
  { k: 'all', l: 'All' },
  { k: 'deposit', l: 'Deposits' },
  { k: 'withdraw', l: 'Withdrawals' },
];

export function WalletActivityScreen() {
  const [filter, setFilter] = useState<Kind>('all');
  const [selected, setSelected] = useState<WalletItem | null>(null);

  const deposits = useQuery({
    queryKey: ['wallet-deposits', 1],
    queryFn: () => homeRepo.deposits(1, 100),
  });
  const withdrawals = useQuery({
    queryKey: ['wallet-withdrawals', 1],
    queryFn: () => homeRepo.withdrawals(1, 100),
  });

  const items = useMemo<WalletItem[]>(() => {
    const ds = (deposits.data?.records ?? []).map((record) => ({
      kind: 'deposit' as const,
      id: `d-${record.id ?? record.datetime}`,
      time: record.datetime ?? 0,
      record,
    }));
    const ws = (withdrawals.data?.records ?? []).map((record) => ({
      kind: 'withdraw' as const,
      id: `w-${record.id ?? record.datetime}`,
      time: record.datetime ?? 0,
      record,
    }));
    return [...ds, ...ws].filter(isSuccessful).sort((a, b) => b.time - a.time);
  }, [deposits.data, withdrawals.data]);

  const visible = useMemo(() => {
    if (filter === 'all') return items;
    return items.filter(i => i.kind === filter);
  }, [items, filter]);
  const loading = deposits.isPending || withdrawals.isPending;
  const error = deposits.isError || withdrawals.isError;

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar title="Wallet Activity" sub="Deposits and withdrawals" onBack={() => history.back()}/>

      <div style={{ display: 'flex', gap: 8, padding: '14px 20px', overflowX: 'auto' }}>
        {filters.map(f => (
          <Chip key={f.k} active={filter === f.k} onClick={() => setFilter(f.k)}>{f.l}</Chip>
        ))}
      </div>

      {error && (
        <ErrorBox
          message="Couldn't load wallet activity."
          onRetry={() => {
            void deposits.refetch();
            void withdrawals.refetch();
          }}
        />
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner/></div>
      ) : visible.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textDim, fontSize: 13 }}>
          No successful wallet activity yet.
        </div>
      ) : (
        <div style={{ padding: '0 20px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {visible.map(item => (
            <WalletRow key={item.id} item={item} onClick={() => setSelected(item)}/>
          ))}
        </div>
      )}

      {selected && <WalletDetail item={selected} onClose={() => setSelected(null)}/>}
    </div>
  );
}

function WalletRow({ item, onClick }: { item: WalletItem; onClick: () => void }) {
  const isDeposit = item.kind === 'deposit';
  const record = item.record;
  const status = displayStatus(record.status ?? (isDeposit ? 'Pending' : 'AwaitingApproval'));
  const amount = Number(record.coinamount ?? 0);
  const success = isSuccessful(item);

  return (
    <button onClick={onClick} style={{
      ...card, padding: 14, display: 'flex', alignItems: 'center', gap: 12,
      width: '100%', cursor: 'pointer', textAlign: 'left', color: T.text,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 12,
        background: success ? 'rgba(57,229,140,0.12)' : isDeposit ? 'rgba(57,229,140,0.08)' : 'rgba(198,255,61,0.10)',
        color: success ? T.ok : isDeposit ? T.ok : T.accent,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {sized(isDeposit ? Icons.down : Icons.up, 20, 2.3)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
          {isDeposit ? 'Deposit' : 'Withdraw'} #{record.id ?? ''}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 8px', marginTop: 4,
          fontSize: 11, color: T.textDim, fontFamily: T.fontMono }}>
          <span>{record.network}</span>
          <span style={{ color: T.line2 }}>·</span>
          <span>{record.cointype}</span>
          <span style={{ color: T.line2 }}>·</span>
          <span style={{ color: success ? T.ok : T.textDim, fontWeight: success ? 700 : 500 }}>{status}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CoinGlyph size={11}/>
          <span style={{ fontFamily: T.fontMono, fontSize: 13, color: T.text,
            fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{fmt(Math.round(amount))}</span>
        </div>
        <span style={{ fontSize: 10, color: T.textMute }}>{fmtDateTime(record.datetime ?? 0)}</span>
      </div>
    </button>
  );
}

function WalletDetail({ item, onClose }: { item: WalletItem; onClose: () => void }) {
  const isDeposit = item.kind === 'deposit';
  const r = item.record;
  const success = isSuccessful(item);
  const rows = isDeposit
    ? depositRows(r as DepositRecord)
    : withdrawRows(r as WithdrawRecord);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Wallet transaction detail"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 120,
        background: 'rgba(0,0,0,0.58)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '18px 18px calc(var(--safe-bottom, 0px) + 18px)',
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        width: 'min(100%, 560px)', maxHeight: '86vh', overflow: 'hidden',
        background: T.bg, border: `1px solid ${T.line2}`, borderRadius: 18,
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: 16, borderBottom: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 42, height: 42, borderRadius: 14,
            background: success ? 'rgba(57,229,140,0.12)' : isDeposit ? 'rgba(57,229,140,0.08)' : 'rgba(198,255,61,0.10)',
            color: success ? T.ok : isDeposit ? T.ok : T.accent,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {sized(isDeposit ? Icons.down : Icons.up, 21, 2.4)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>
              {isDeposit ? 'Deposit' : 'Withdrawal'} #{r.id ?? ''}
            </div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>{fmtDateTime(r.datetime ?? 0)}</div>
          </div>
          <button onClick={onClose} style={btnIcon} aria-label="Close">{sized(Icons.x, 18)}</button>
        </div>

        <div style={{ overflowY: 'auto', padding: 16 }}>
          <div style={{ ...card, overflow: 'hidden' }}>
            {rows.map((row, i) => (
              <DetailRow key={row.label} label={row.label} value={row.value} mono={row.mono} last={i === rows.length - 1}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function depositRows(r: DepositRecord) {
  return [
    { label: 'Status', value: displayStatus(r.status) },
    { label: 'Network', value: r.network ?? '—' },
    { label: 'Coin', value: r.cointype ?? '—' },
    { label: 'USD amount', value: `$${Number(r.usdamount ?? 0).toFixed(2)}`, mono: true },
    { label: 'Coin amount', value: `${fmt(Math.round(Number(r.coinamount ?? 0)))} C`, mono: true },
    { label: 'Received', value: `${fmt(Math.round(Number(r.receivedcoins ?? 0)))} C`, mono: true },
    { label: 'Address', value: r.address || '—', mono: true },
    { label: 'Provider ref', value: r.providerref || '—', mono: true },
    { label: 'Expires', value: r.expiresat ? fmtDateTime(r.expiresat) : '—' },
    { label: 'Created', value: r.datetime ? fmtDateTime(r.datetime) : '—' },
  ];
}

function withdrawRows(r: WithdrawRecord) {
  return [
    { label: 'Status', value: displayStatus(r.status) },
    { label: 'Network', value: r.network ?? '—' },
    { label: 'Coin', value: r.cointype ?? '—' },
    { label: 'Coin amount', value: `${fmt(Math.round(Number(r.coinamount ?? 0)))} C`, mono: true },
    { label: 'Fee', value: `${fmt(Math.round(Number(r.fee ?? 0)))} C`, mono: true },
    { label: 'Total charged', value: `${fmt(Number(r.coinscharged ?? 0))} C`, mono: true },
    { label: 'USD amount', value: `$${Number(r.usdamount ?? 0).toFixed(2)}`, mono: true },
    { label: 'Destination', value: r.destinationaddress || '—', mono: true },
    { label: 'Provider ref', value: r.providerref || '—', mono: true },
    { label: 'Request ref', value: r.requestref || '—', mono: true },
    { label: 'Created', value: r.datetime ? fmtDateTime(r.datetime) : '—' },
  ];
}

function isSuccessful(item: WalletItem) {
  const status = normalizeStatus(item.record.status);
  if (item.kind === 'deposit') {
    return status === 'settled' || status === 'completed' || status === 'complete' || status === 'success' || status === 'finished';
  }
  return status === 'completed' || status === 'complete' || status === 'success' || status === 'finished' || status === 'settled';
}

function normalizeStatus(status?: string | null) {
  return String(status ?? '').replace(/[\s_-]+/g, '').toLowerCase();
}

function displayStatus(status?: string | null) {
  const raw = String(status ?? '').trim();
  if (!raw) return '—';
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function DetailRow({ label, value, mono, last }: { label: string; value: string; mono?: boolean; last?: boolean }) {
  return (
    <div style={{
      padding: '13px 16px', borderBottom: last ? 'none' : `1px solid ${T.line}`,
      display: 'flex', justifyContent: 'space-between', gap: 14,
    }}>
      <span style={{ fontSize: 11, color: T.textMute, textTransform: 'uppercase',
        letterSpacing: 0.8, fontWeight: 600, flexShrink: 0 }}>{label}</span>
      <span style={{
        fontSize: 12, color: T.text, fontFamily: mono ? T.fontMono : T.font,
        textAlign: 'right', overflowWrap: 'anywhere', minWidth: 0,
      }}>{value}</span>
    </div>
  );
}
